import { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Icon,
  InputAdornment,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import CloseIcon from '@mui/icons-material/Close'
import { addDays, format } from 'date-fns'
//local imports
import './jobAdForm.css'
import AdxInput from 'components/custom/AdxInput'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxDropdown from 'components/custom/AdxDropdown'
import { DEFAULT_FORM_VALUES, jobAdValidation } from 'module/recruitment/utils/jobAdValidation'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxDropdownWithFilter from 'components/reusable/AdxDropdownWithFilter'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxRadioButton from 'components/custom/AdxRadioButton'
import { FormBox } from 'module/recruitment/pages/recruitment-create-job-ad/components/JobAdForm/FormComponents/JobAdFormComponents'
import { GRADE_LIST, STATE_IN_MALAYSIA_LIST } from 'module/recruitment/resources/jobAdListItem'
import AdxAutocomplete from 'components/reusable/AdxAutocomplete'
import PreviewAdDialog from 'module/recruitment/pages/recruitment-create-job-ad/components/JobAdForm/FormComponents/PreviewAdDialog'
import { useQuery } from 'react-query'
import {
  getCourses,
  getJobBackgrounds,
  getJobLevels,
  getQualifications,
  getSkills,
} from 'services/api/modules/recruitment/dropdown'
import PublishJobAdsDrawer from './PublishJobAdsDrawer'
import SkillRecommender from './FormComponents/SkillRecommender'
import AdxButton from 'components/custom/AdxButton'

const JobAdForm = ({
  isPreviewAd,
  handleClosePreview,
  initialValues,
  onSubmit,
  handleTypeOfForm,
  editJobAds,
  editIsLoading,
  publishJobAds,
  draftIsLoading,
  updateIsLoading,
  handleUpdateJobAds,
  handleFormikValues,
}) => {
  const theme = useTheme()

  //preview job ad drawer
  const [drawerState, setDrawerState] = useState({ right: false })
  const handleDrawer = (anchor, open) => (event) => {
    setDrawerState({ ...drawerState, [anchor]: open })
  }
  //publish job ad drawer
  const [publishDrawerState, setPublishDrawerState] = useState({ right: false })
  const handlePublishDrawer = (anchor, open) => (event) => {
    setPublishDrawerState({ ...publishDrawerState, [anchor]: open })
  }

  // API CALL
  const { data: jobLevelsData } = useQuery('getJobLevels', async () => {
    const { data } = await getJobLevels()
    return data
  })

  const { data: coursesData } = useQuery('getCourses', async () => {
    const { data } = await getCourses()
    return data
  })

  const { data: qualificationsData } = useQuery('getQualifications', async () => {
    const { data } = await getQualifications()
    return data
  })

  const { data: skillsData } = useQuery('getSkills', async () => {
    const { data } = await getSkills()
    return data
  })

  const { data: jobBackData } = useQuery('getJobBackgrounds', async () => {
    const { data } = await getJobBackgrounds()
    return data
  })

  // how many times user clicked publish
  const [formSubmitAttempt, setFormSubmitAttempt] = useState(0)
  // job background options
  const [filteredJobBackgroundId, setFilteredJobBackgroundId] = useState(jobBackData)
  // selected courses
  const [selectedCourses, setSelectedCourses] = useState([])
  // year of experience
  const [experience, setExperience] = useState(0)
  const [isAcceptFreshGrad, setIsAcceptFreshGrad] = useState(false)
  // selected skills
  const [selectedSkills, setSelectedSkills] = useState({})

  //set  initial value for skills
  useEffect(() => {
    if (skillsData && skillsData.length > 0) {
      const skillToBeFilter = initialValues?.skill?.split(', ')

      const filtered =
        skillToBeFilter?.reduce((acc, item, index) => {
          const found = skillsData.find((skill) => skill.name === item)
          if (found) {
            acc[item] = found.id
          } else if (!found && item) {
            acc[item] = skillsData.length + index + 1
          }

          return acc
        }, {}) || {}

      setSelectedSkills(filtered)
    }
  }, [skillsData, initialValues])

  //set  initial value for course
  useEffect(() => {
    if (coursesData && coursesData.length > 0) {
      const courseToBeFilter = initialValues?.course?.split(',')
      const filtered = coursesData?.filter((item) => courseToBeFilter?.includes(item.course))
      setSelectedCourses(filtered)
    }
  }, [coursesData, initialValues])

  // set initial value for experience
  useEffect(() => {
    setExperience(initialValues?.year_of_experience ?? 0)
    if (initialValues?.fresh_grade_placement === 1) {
      setIsAcceptFreshGrad(true)
    }
  }, [initialValues])

  // common style for horizontal radio group buttons
  const horizontalButtonStyle = {
    display: 'flex',
    justifyContent: 'center',
    flexGrow: 1,
    borderRadius: 0,
    height: theme.componentHeight.standard,
  }

  // change initial value for string to "" if it is null
  const updatedIntialValues = {}
  for (const key in initialValues) {
    if (key === 'skill') {
      updatedIntialValues[key] = initialValues[key] === null ? '' : initialValues[key].split(', ')
    } else {
      updatedIntialValues[key] = initialValues[key] === null ? '' : initialValues[key]
    }
  }

  //set initial date for edit job ads
  const dateString = initialValues?.expired_date
  const date = new Date(dateString)
  const formattedDate = date?.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  return (
    <Formik
      enableReinitialize={true}
      validateOnBlur={false}
      validateOnChange={true}
      initialValues={initialValues ? updatedIntialValues : DEFAULT_FORM_VALUES}
      validationSchema={Yup.object().shape(jobAdValidation)}
      onSubmit={onSubmit}
    >
      {({ values, touched, errors, isValid, handleChange, handleSubmit, setFieldValue }) => {
        // for save button at header
        handleFormikValues(values)

        const handleExperience = (e) => {
          setFieldValue('year_of_experience', parseInt(e.target.value))
          if (e.target.value === 1) {
            setFieldValue('fresh_grade_placement', 1)
          } else {
            setFieldValue('fresh_grade_placement', 0)
          }
        }
        const handleFreshGrade = (data) => {
          if (data === 1) {
            setFieldValue('fresh_grade_placement', 1)
          } else {
            setFieldValue('fresh_grade_placement', 0)
          }
        }

        // course selection change handler
        const handleSelectCourse = (e, value) => {
          if (!selectedCourses.includes(value)) {
            let courses = [value, ...selectedCourses]
            setSelectedCourses(courses)
            let courseArray = courses.map((item) => item.course)
            setFieldValue('course', courseArray)
          }
        }

        // course delete selecton handler
        const handleDeleteCourseSelection = (target) => {
          let courses = selectedCourses.filter((item) => item !== target)
          setSelectedCourses(courses)
          let courseArray = courses.map((item) => item.course)
          setFieldValue('course', courseArray)
        }

        //experience increment or decrement
        const handleCalculateExperience = (arg) => {
          let data = experience + arg
          setExperience(data)
          setFieldValue('year_of_experience', data)
        }
        //experience accept fresh graduate handler
        const handleResetExperience = (e, value) => {
          setIsAcceptFreshGrad(value)
          setExperience(1)
          setFieldValue('year_of_experience', 1)
          setFieldValue('fresh_grade_placement', value ? 1 : 0)
        }

        // skills selection change handler
        const handleSelectSkills = (e, value) => {
          const skillToAdd = value.custom ? { id: new Date().getTime(), name: value.name } : value
          const skills = selectedSkills
          if (!skills[skillToAdd.name]) {
            skills[skillToAdd.name] = skillToAdd.id
          }

          const skillArray = Object.keys(skills).map((item) => item)
          setFieldValue('skill', skillArray)
        }

        const handleCheckCustomSkill = (options, params) => {
          const filtered = options.filter((item) =>
            item.name.toLowerCase().includes(params.inputValue.toLowerCase())
          )
          const isExisting = options.some(
            (option) => option.name.toLowerCase() === params.inputValue.toLowerCase()
          )
          if (!isExisting && params.inputValue !== '') {
            filtered.unshift({
              name: params.inputValue,
              custom: true,
            })
          }
          return filtered
        }

        const renderOption = (props, options) => (
          <li {...props}>
            {options.custom && (
              <Typography
                sx={{
                  mr: 1,
                  backgroundColor: 'alternate.main',
                  color: 'white.main',
                  px: 1,
                  borderRadius: 1,
                }}
              >
                Add
              </Typography>
            )}
            {<Typography>{options.name?.split('"')[1] || options.name}</Typography>}
          </li>
        )
        // skills delete selection handler
        const handleDeleteSkillSelection = (target) => {
          const skills = selectedSkills
          delete skills[target]

          setSelectedSkills(skills)
          let skillArray = Object.keys(skills).map((item) => item)
          setFieldValue('skill', skillArray)
        }

        if (editIsLoading || draftIsLoading || updateIsLoading) {
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: {
                  xs: 5 / 6,
                  sm: 3 / 5,
                },
                height: '100vh',
              }}
            >
              <CircularProgress color="alternate" />
            </Box>
          )
        } else {
          return (
            <Form>
              <Box sx={{ display: `flex`, flexDirection: `row` }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxWidth: `825px`,
                    zIndex: 10,
                    paddingBottom: 11,
                  }}
                >
                  {/* Job details */}
                  <FormBox title="Job Details">
                    {/* Position Title */}
                    <Field
                      as={AdxInput}
                      disabled={editJobAds}
                      name="position_title"
                      label={
                        <Box display="flex">
                          <Typography variant="body1" color="dark-text.main">
                            Position Title
                          </Typography>
                          <Typography variant="body1" color="error.main">
                            *
                          </Typography>
                        </Box>
                      }
                      placeholder="e.g. Software Engineer"
                      isErrorShown={touched.position_title && Boolean(errors.position_title)}
                      errorMessage={<AdxErrorMessage>{errors.position_title}</AdxErrorMessage>}
                      value={values.position_title}
                      required
                    />

                    {/* Job Level */}
                    <Field
                      as={AdxDropdown}
                      name="job_level_id"
                      disabled={editJobAds}
                      label={
                        <Box display="flex">
                          <Typography variant="body1" color="dark-text.main">
                            Job Level
                          </Typography>
                          <Typography variant="body1" color="error.main">
                            *
                          </Typography>
                        </Box>
                      }
                      placeholder="Select"
                      value={values.job_level_id}
                      isErrorShown={touched.job_level_id && Boolean(errors.job_level_id)}
                      errorMessage={<AdxErrorMessage>{errors.job_level_id}</AdxErrorMessage>}
                    >
                      {jobLevelsData?.map((e) => (
                        <AdxMenuItem key={e.id} value={e.id} selectedvalue={values.job_level_id}>
                          {e.level_name}
                        </AdxMenuItem>
                      ))}
                    </Field>

                    {/* Job Background */}
                    <Field
                      as={AdxDropdownWithFilter}
                      disabled={editJobAds}
                      placeholder="Select"
                      name="job_background_id"
                      label={
                        <Box display="flex">
                          <Typography variant="body1" color="dark-text.main">
                            Specialization
                          </Typography>
                          <Typography variant="body1" color="error.main">
                            *
                          </Typography>
                        </Box>
                      }
                      value={values.job_background_id}
                      allOptions={jobBackData}
                      setOptions={setFilteredJobBackgroundId}
                      filters={['job_background_name']}
                      isErrorShown={touched.job_background_id && Boolean(errors.job_background_id)}
                      errorMessage={<AdxErrorMessage>{errors.job_background_id}</AdxErrorMessage>}
                    >
                      {filteredJobBackgroundId?.map((e) => (
                        <AdxMenuItem key={e} value={e.id} selectedvalue={values.job_background_id}>
                          {e.job_background_name}
                        </AdxMenuItem>
                      ))}
                    </Field>

                    {/* Employment Type */}
                    <Box>
                      <Box display="flex">
                        <Typography variant="body1" mb={1}>
                          Employment Type
                        </Typography>
                        <Typography variant="body1" mb={1} color="error.main">
                          *
                        </Typography>
                      </Box>
                      <Field as={RadioGroup} name="position_type" value={values.position_type}>
                        <Box
                          border={touched.position_type && Boolean(errors.position_type) ? 1 : 0}
                          borderRadius="5px"
                          borderColor="red"
                          display="flex"
                        >
                          <AdxFormControlLabel
                            disabled={editJobAds}
                            type="outlined"
                            control={<AdxRadioButton type="no-radio" />}
                            label="Full time"
                            value="full-time"
                            selected={values.position_type}
                            sx={{ ...horizontalButtonStyle, borderRadius: '5px 0 0 5px' }}
                          />
                          <AdxFormControlLabel
                            disabled={editJobAds}
                            type="outlined"
                            control={<AdxRadioButton type="no-radio" />}
                            label="Contract"
                            value="contract"
                            selected={values.position_type}
                            sx={horizontalButtonStyle}
                          />
                          <AdxFormControlLabel
                            disabled={editJobAds}
                            type="outlined"
                            control={<AdxRadioButton type="no-radio" />}
                            label="Part time"
                            value="part-time"
                            selected={values.position_type}
                            sx={{ ...horizontalButtonStyle, borderRadius: '0 5px 5px 0' }}
                          />
                        </Box>
                        {errors.position_type ? (
                          <AdxErrorMessage>{errors.position_type}</AdxErrorMessage>
                        ) : null}
                      </Field>
                    </Box>

                    {/* Salary */}
                    <Box>
                      <Box display="flex">
                        <Typography variant="body1" mb={1}>
                          Salary
                        </Typography>
                        <Typography variant="body1" mb={1} color="error.main">
                          *
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        sx={{
                          width: {
                            xs: '100%',
                            sm: 1 / 2,
                          },
                          minWidth: {
                            xs: '100%',
                            sm: '280px',
                          },
                        }}
                      >
                        <Field
                          as={AdxInput}
                          disabled={editJobAds}
                          name="min_salary"
                          value={values.min_salary}
                          placeholder="Min."
                          startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                          sx={{ flexGrow: 1 }}
                          type="number"
                          min="0"
                          isErrorShown={touched.min_salary && Boolean(errors.min_salary)}
                          errorMessage={<AdxErrorMessage>{errors.min_salary}</AdxErrorMessage>}
                        />
                        <Typography variant="body1" paddingX={3} sx={{ margin: 'auto' }}>
                          -
                        </Typography>
                        <Field
                          as={AdxInput}
                          disabled={editJobAds}
                          name="max_salary"
                          value={values.max_salary}
                          placeholder="Max."
                          startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                          sx={{ flexGrow: 1 }}
                          type="number"
                          min="0"
                          isErrorShown={touched.max_salary && Boolean(errors.max_salary)}
                          errorMessage={<AdxErrorMessage>{errors.max_salary}</AdxErrorMessage>}
                        />
                      </Box>
                    </Box>
                  </FormBox>

                  {/* Job location */}
                  <FormBox title="Work Location">
                    {/* Address */}
                    <Field
                      as={AdxInput}
                      disabled={editJobAds}
                      name="work_location"
                      label={
                        <Box display="flex">
                          <Typography variant="body1" color="dark-text.main">
                            Address
                          </Typography>
                          <Typography variant="body1" color="error.main">
                            *
                          </Typography>
                        </Box>
                      }
                      value={values.work_location}
                      isErrorShown={touched.work_location && Boolean(errors.work_location)}
                      errorMessage={<AdxErrorMessage>{errors.work_location}</AdxErrorMessage>}
                    />

                    {/* City */}
                    <Box
                      sx={{
                        display: {
                          sm: 'flex',
                        },
                        columnGap: {
                          sm: 3,
                        },
                      }}
                    >
                      <Field
                        as={AdxInput}
                        name="city"
                        disabled={editJobAds}
                        label={
                          <Box display="flex">
                            <Typography variant="body1" color="dark-text.main">
                              City
                            </Typography>
                            <Typography variant="body1" color="error.main">
                              *
                            </Typography>
                          </Box>
                        }
                        value={values.city}
                        isErrorShown={touched.city && Boolean(errors.city)}
                        errorMessage={<AdxErrorMessage>{errors.city}</AdxErrorMessage>}
                        sx={{
                          width: {
                            sm: 2 / 5,
                          },
                        }}
                      />

                      {/* State */}
                      <Field
                        as={AdxDropdown}
                        name="state"
                        disabled={editJobAds}
                        label={
                          <Box display="flex">
                            <Typography variant="body1" color="dark-text.main">
                              State
                            </Typography>
                            <Typography variant="body1" color="error.main">
                              *
                            </Typography>
                          </Box>
                        }
                        value={values.state}
                        isErrorShown={touched.state && Boolean(errors.state)}
                        errorMessage={<AdxErrorMessage>{errors.state}</AdxErrorMessage>}
                        sx={{
                          width: {
                            sm: 3 / 5,
                          },
                        }}
                      >
                        {STATE_IN_MALAYSIA_LIST.map((e) => (
                          <AdxMenuItem key={e.id} value={e.name} selectedvalue={values.state}>
                            {e.name}
                          </AdxMenuItem>
                        ))}
                      </Field>
                    </Box>
                  </FormBox>

                  {/* Job Description */}
                  <FormBox title={'Job Description'}>
                    <Field
                      as={AdxInput}
                      name="job_description"
                      value={values.job_description}
                      label={
                        <Box display="flex">
                          <Typography variant="body1" color="dark-text.main">
                            Describe the responsibilities of this job
                          </Typography>
                          <Typography variant="body1" color="error.main">
                            *
                          </Typography>
                        </Box>
                      }
                      multiline
                      minRows={15}
                      maxRows={15}
                      charLimit={2000}
                      placeholder="Fill in your job description here"
                      isErrorShown={touched.job_description && Boolean(errors.job_description)}
                      errorMessage={<AdxErrorMessage>{errors.job_description}</AdxErrorMessage>}
                      sxInput={{
                        py: 2,
                      }}
                    />
                  </FormBox>

                  {/* Job Requirements */}
                  <FormBox title="Job Requirements">
                    {/* Qualifications */}
                    <Field
                      as={AdxDropdown}
                      disabled={editJobAds}
                      name="qualification_id"
                      label={
                        <Box display="flex">
                          <Typography variant="body1" color="dark-text.main">
                            Qualifications
                          </Typography>
                          <Typography variant="body1" color="error.main">
                            *
                          </Typography>
                        </Box>
                      }
                      placeholder="Select Qualifications"
                      value={values.qualification_id}
                      onChange={(e) => {
                        // if selected SPM then reset grade and selected courses
                        if (e.target.value < 2) {
                          setFieldValue('grade_requirement', '')
                          setSelectedCourses([])
                          setFieldValue('course', [])
                        }
                        handleChange(e)
                      }}
                      isErrorShown={touched.qualification_id && Boolean(errors.qualification_id)}
                      errorMessage={<AdxErrorMessage>{errors.qualification_id}</AdxErrorMessage>}
                    >
                      {qualificationsData?.map((e) => (
                        <AdxMenuItem
                          key={e.id}
                          value={e.id}
                          selectedvalue={values.qualification_id}
                        >
                          {e.qualification_name}
                        </AdxMenuItem>
                      ))}
                    </Field>

                    {/* Experience */}
                    <Box>
                      {/* <Box 
                  display="flex"
                  >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Minimum working experience 
                    </Typography>
                    <Typography variant="body1" color="error.main" sx={{ mb: 1 }}>
                      *
                    </Typography>
                  </Box> */}

                      <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                        <Box
                          display="flex"
                          sx={{
                            xs: '100%',
                          }}
                        >
                          {/* <Box>
                        <Box display="flex">
                          <AdxButton
                            disabled={experience <= 0 && isAcceptFreshGrad}
                            sx={{ borderRadius: '5px 0 0 5px' }}
                            onClick={() => handleCalculateExperience(-1)}
                          >
                            <Typography variant="h3">-</Typography>
                          </AdxButton>

                          <Field
                            as={InputBase}
                            name="year_of_experience"
                            readOnly
                            value={experience}
                            sx={{
                              '&.MuiInputBase-root': {
                                outline: 0,
                                border: '1px solid',
                                color: 'light-gray-text.main',
                                display: 'flex',
                                justifyContent: 'center',
                              },
                              '& .MuiInputBase-input': {
                                textAlign: 'center',
                              },
                            }}
                          />

                          <AdxButton
                            disabled={isAcceptFreshGrad}
                            sx={{
                              borderRadius: '0 5px 5px 0',
                            }}
                            onClick={() => handleCalculateExperience(1)}
                          >
                            <Typography variant="h3">+</Typography>
                          </AdxButton>
                        </Box>
                        {errors.year_of_experience ? (
                          <AdxErrorMessage>{errors.year_of_experience}</AdxErrorMessage>
                        ) : null}
                      </Box> 

                      <Typography
                        variant="body2"
                        sx={{ alignSelf: 'center', ml: 2, color: 'light-gray-text.main' }}
                      >
                        year(s)
                      </Typography>*/}

                          {/* Minimum working experience  */}
                          <Box>
                            <Field
                              as={AdxDropdown}
                              name="year_of_experience"
                              disabled={editJobAds}
                              sx={{ width: 460 }}
                              // onChange={handleExperience}
                              label={
                                <Box display="flex">
                                  <Typography variant="body1" color="dark-text.main">
                                    Minimum Working Experience
                                  </Typography>
                                  <Typography variant="body1" color="error.main">
                                    *
                                  </Typography>
                                </Box>
                              }
                              placeholder="Select"
                              value={values.year_of_experience}
                              isErrorShown={
                                touched.year_of_experience && Boolean(errors.year_of_experience)
                              }
                              errorMessage={
                                <AdxErrorMessage>{errors.year_of_experience}</AdxErrorMessage>
                              }
                            >
                              <AdxMenuItem value={1}>0-1 year</AdxMenuItem>
                              <AdxMenuItem
                                value={2}
                                //  selectedvalue={values.year_of_experience}
                              >
                                1-3 years
                              </AdxMenuItem>
                              <AdxMenuItem
                                value={3}
                                //  selectedvalue={values.year_of_experience}
                              >
                                3-5 years
                              </AdxMenuItem>
                              <AdxMenuItem
                                value={4}
                                // selectedvalue={values.year_of_experience}
                              >
                                More than 5 years
                              </AdxMenuItem>
                            </Field>
                          </Box>
                        </Box>

                        {/* Fresh Grad Placement
                    <Field
                      as={AdxFormControlLabel}
                      name="fresh_grade_placement"
                      type="no-fill"
                      control={<AdxCheckbox />}
                      checked={isAcceptFreshGrad}
                      label="We welcome fresh graduates to apply"
                      value={isAcceptFreshGrad ? 1 : 0}
                      onChange={(e, value) => handleResetExperience(e, value)}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: 12,
                        },
                      }}
                    /> */}
                      </Box>
                    </Box>

                    {/* Job Requirements */}
                    <Box>
                      <Field
                        as={AdxInput}
                        name="job_requirement"
                        label={
                          <Box display="flex">
                            <Typography variant="body1" color="dark-text.main">
                              Requirement
                            </Typography>
                            <Typography variant="body1" color="error.main">
                              *
                            </Typography>
                          </Box>
                        }
                        value={values.job_requirement}
                        multiline
                        minRows={15}
                        maxRows={15}
                        charLimit={2000}
                        placeholder="Fill in your job requirements here"
                        isErrorShown={touched.job_requirement && Boolean(errors.job_requirement)}
                        errorMessage={<AdxErrorMessage>{errors.job_requirement}</AdxErrorMessage>}
                        sxInput={{
                          py: 2,
                        }}
                      />
                    </Box>
                  </FormBox>

                  {/* Required skills */}
                  <FormBox title={'Job Skills Required (Minimum 3 Skills)'}>
                    <Box>
                      <Box display="flex" mb={1}>
                        <Typography variant="body1" color="dark-text.main">
                          Add skills
                        </Typography>
                        <Typography variant="body1" color="error.main">
                          *
                        </Typography>
                      </Box>
                      <Field
                        as={AdxAutocomplete}
                        freeSolo
                        forcePopupIcon
                        autoHighlight
                        renderOption={renderOption}
                        name="skill"
                        onChange={(e, value) => handleSelectSkills(e, value)}
                        options={skillsData ?? []}
                        filterOptions={handleCheckCustomSkill}
                        icon="search"
                        getOptionLabel={(option) => option.name || ''}
                        placeholder="Select Skills"
                        disableClearable
                        filterSelectedOptions
                        isOptionEqualToValue={(option, value) => value.includes(option.name)}
                        isErrorShown={errors.skill && touched.skill}
                        errorMessage={<AdxErrorMessage>{errors.skill}</AdxErrorMessage>}
                      />
                    </Box>
                    <SkillRecommender
                      handleSetSelectedSkills={(value) => handleSelectSkills(null, value)}
                      formData={values}
                      skillsTotal={skillsData?.length + Object.keys(selectedSkills)?.length || 0}
                      disabled={!values.job_requirement || !values.job_description}
                      isEdit={editJobAds}
                    />
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      gap={1}
                      maxHeight="90px"
                      overflow="auto"
                      mt={1}
                    >
                      {Object.keys(selectedSkills).map((key) => (
                        <Chip
                          key={selectedSkills[key]}
                          label={key}
                          variant="outlined"
                          onDelete={() => handleDeleteSkillSelection(key)}
                          sx={{
                            border: 0,
                            color: 'dark-text.main',
                            bgcolor: 'default-background.main',
                            '& 	.MuiChip-deleteIcon': {
                              fontSize: 20,
                              color: 'alternate.main',
                              '&:hover': { color: 'green-02.main' },
                            },
                          }}
                          deleteIcon={<CloseIcon />}
                        />
                      ))}
                    </Box>
                  </FormBox>

                  {/* Ads Duration */}
                  <FormBox title="Job Duration">
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      This job advertisement will be visible to jobseekers until it expires. You can
                      still deactivate this job advertisement anytime you want
                    </Typography>
                    <Box display="flex">
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Job Duration
                      </Typography>
                      <Typography variant="body1" color="error.main" sx={{ mb: 1 }}>
                        *
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Field
                        as={RadioGroup}
                        name="job_advertisement_duration"
                        value={values.job_advertisement_duration}
                      >
                        <Box
                          border={
                            touched.job_advertisement_duration &&
                            Boolean(errors.job_advertisement_duration)
                              ? 1
                              : 0
                          }
                          borderRadius="5px"
                          borderColor="red"
                          display="flex"
                          sx={{
                            width: {
                              xs: 250,
                              sm: 300,
                              lg: 500,
                            },
                          }}
                        >
                          <AdxFormControlLabel
                            disabled={editJobAds}
                            type="outlined"
                            control={<AdxRadioButton type="no-radio" />}
                            label="3 months"
                            value={90}
                            selected={parseInt(values.job_advertisement_duration)}
                            sx={{ ...horizontalButtonStyle, borderRadius: '5px 0 0 5px' }}
                          />
                          <AdxFormControlLabel
                            disabled={editJobAds}
                            type="outlined"
                            control={<AdxRadioButton type="no-radio" />}
                            label="6 months"
                            value={180}
                            selected={parseInt(values.job_advertisement_duration)}
                            sx={horizontalButtonStyle}
                          />
                          <AdxFormControlLabel
                            disabled={editJobAds}
                            type="outlined"
                            control={<AdxRadioButton type="no-radio" />}
                            label="9 months"
                            value={270}
                            selected={parseInt(values.job_advertisement_duration)}
                            sx={{ ...horizontalButtonStyle, borderRadius: '0 5px 5px 0' }}
                          />
                        </Box>
                      </Field>
                      <Box
                        bgcolor="default-background.main"
                        height={45}
                        display="flex"
                        alignItems="center"
                        sx={{
                          width: {
                            xs: 100,
                            sm: 200,
                            lg: 350,
                          },
                          borderRadius: '5px',
                        }}
                      >
                        {editJobAds ? (
                          <Typography
                            variant="body2"
                            ml={2}
                            display="flex"
                            alignItems="center"
                            color="light-gray-text.main"
                          >
                            <Icon>calendar_month_icon</Icon>
                            Expires on: {formattedDate}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            ml={2}
                            display="flex"
                            alignItems="center"
                            color="light-gray-text.main"
                          >
                            <Icon>calendar_month_icon</Icon>
                            Expires on:{' '}
                            {values.job_advertisement_duration
                              ? format(
                                  addDays(new Date(), values.job_advertisement_duration),
                                  'dd MMM yyyy'
                                )
                              : null}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {errors.job_advertisement_duration ? (
                      <AdxErrorMessage>{errors.job_advertisement_duration}</AdxErrorMessage>
                    ) : null}
                  </FormBox>

                  <Box
                    position={'fixed'}
                    bottom={0}
                    left={0}
                    paddingY={2}
                    paddingX={4}
                    bgcolor={'primary.light'}
                    width={'100%'}
                    borderTop={'1px solid'}
                    borderColor={'grayscale.line-light'}
                  >
                    {/* Form Actions */}
                    {editJobAds === false && publishJobAds === false ? (
                      <Box display="flex" columnGap={2} justifyContent="flex-end">
                        <AdxButton
                          variant={'outlined'}
                          size="standard"
                          onClick={() => {
                            handleSubmit()
                            handleTypeOfForm('draft')
                          }}
                        >
                          Save as draft
                        </AdxButton>
                        <AdxButton
                          variant={'contained'}
                          size="standard"
                          sx={{
                            px: {
                              xs: 4,
                              sm: 12,
                            },
                          }}
                          onClick={() => {
                            setFormSubmitAttempt((prev) => prev + 1)
                            handleSubmit()
                            handleTypeOfForm('publish')
                          }}
                        >
                          Publish
                        </AdxButton>
                      </Box>
                    ) : null}

                    {editJobAds ? (
                      <Box display="flex" columnGap={2} justifyContent="flex-end">
                        <AdxButton
                          variant={'contained'}
                          size="standard"
                          sx={{
                            px: {
                              xs: 4,
                              sm: 12,
                            },
                          }}
                          onClick={() => {
                            handleSubmit()
                          }}
                        >
                          Save
                        </AdxButton>
                      </Box>
                    ) : null}

                    {publishJobAds ? (
                      <Box display="flex" columnGap={2} justifyContent="space-between">
                        <AdxButton
                          variant={'outlined'}
                          size="standard"
                          onClick={handleDrawer('right', true)}
                        >
                          Preview
                        </AdxButton>
                        <Box display="flex" columnGap={2}>
                          <AdxButton
                            variant={'outlined'}
                            size="standard"
                            //  innerRef={saveButtonRef}

                            // onClick={() => {
                            //   handleSubmit()
                            //   handleTypeOfForm("update")
                            // }}
                            onClick={() => {
                              handleUpdateJobAds(values)
                            }}
                          >
                            Save
                          </AdxButton>
                          <AdxButton
                            variant={'contained'}
                            size="standard"
                            sx={{
                              px: {
                                xs: 4,
                                sm: 12,
                              },
                            }}
                            // onClick={() => {
                            //   handleSubmit()
                            //   handleTypeOfForm("publish")
                            // }}
                            onClick={handlePublishDrawer('right', true)}
                          >
                            Publish
                          </AdxButton>
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                  {/* Preview Ad drawer (use to be a dialog) */}
                  <PreviewAdDialog
                    editJobAds={editJobAds}
                    drawerState={
                      JSON.stringify(isPreviewAd) === '{"right":true}' ? isPreviewAd : drawerState
                    }
                    handleDrawer={
                      JSON.stringify(isPreviewAd) === '{"right":true}'
                        ? handleClosePreview
                        : handleDrawer
                    }
                    data={{
                      formData: values,
                      position_level: jobLevelsData?.filter((e) => e.id === values.job_level_id),
                      job_specialization: jobBackData?.filter(
                        (e) => e.id === values.job_background_id
                      ),
                      qualification: qualificationsData?.filter(
                        (e) => e.id === values.qualification_id
                      ),
                      grade: GRADE_LIST.filter((e) => e.value === values.grade_requirement),
                      courses: coursesData?.filter((e) => values.course?.includes(e.course)),
                      skills: selectedSkills,
                    }}
                  />

                  {/* publish Ad Dialog */}
                  <PublishJobAdsDrawer
                    handleSubmit={handleSubmit}
                    handleTypeOfForm={handleTypeOfForm}
                    isValid={isValid}
                    errors={errors}
                    drawerState={publishDrawerState}
                    handleDrawer={handlePublishDrawer}
                    data={{
                      formData: values,
                      position_level: jobLevelsData?.filter((e) => e.id === values.job_level_id),
                      job_specialization: jobBackData?.filter(
                        (e) => e.id === values.job_background_id
                      ),
                      qualification: qualificationsData?.filter(
                        (e) => e.id === values.qualification_id
                      ),
                      grade: GRADE_LIST.filter((e) => e.value === values.grade_requirement),
                      courses: coursesData?.filter((e) => values.course?.includes(e.course)),
                      skills: skillsData?.filter((e) => values.skill?.includes(e.name)),
                    }}
                  />
                </Box>
                {/* right progress bar  */}
                {publishJobAds ? (
                  <Box
                    item
                    sx={{
                      position: `fixed`,
                      top: 80,
                      right: 50,
                      display: { sm: `none`, lg: `block` },
                    }}
                  >
                    <Box
                      sx={{
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        borderLeft: '5px solid',
                        height: '40px',
                        gap: 1,
                        borderLeftColor:
                          values.position_title !== null &&
                          values.position_title !== '' &&
                          errors.position_title === undefined &&
                          values.job_level_id !== null &&
                          values.job_level_id !== '' &&
                          errors.job_level_id === undefined &&
                          values.position_type !== null &&
                          values.position_type !== '' &&
                          errors.position_type === undefined &&
                          values.job_background_id !== null &&
                          values.job_background_id !== '' &&
                          errors.job_background_id === undefined &&
                          values.max_salary !== null &&
                          values.max_salary !== '' &&
                          errors.max_salary === undefined &&
                          values.min_salary !== null &&
                          values.min_salary !== '' &&
                          errors.min_salary === undefined
                            ? 'alternate.main'
                            : 'gray-line.main',
                        transition: 'border-left-color 0.3s linear',
                      }}
                    >
                      {values.position_title !== null &&
                      values.position_title !== '' &&
                      errors.position_title === undefined &&
                      values.job_level_id !== null &&
                      values.job_level_id !== '' &&
                      errors.job_level_id === undefined &&
                      values.position_type !== null &&
                      values.position_type !== '' &&
                      errors.position_type === undefined &&
                      values.job_background_id !== null &&
                      values.job_background_id !== '' &&
                      errors.job_background_id === undefined &&
                      values.max_salary !== null &&
                      values.max_salary !== '' &&
                      errors.max_salary === undefined &&
                      values.min_salary !== null &&
                      values.min_salary !== '' &&
                      errors.min_salary === undefined ? (
                        <Icon sx={{ color: 'positive.main', fontSize: '16px' }}>
                          check_circle_icon
                        </Icon>
                      ) : null}
                      {errors.position_title !== undefined ||
                      errors.job_level_id !== undefined ||
                      errors.position_type !== undefined ||
                      errors.job_background_id !== undefined ||
                      errors.max_salary !== undefined ||
                      errors.min_salary !== undefined ? (
                        <Icon sx={{ color: 'error.main', fontSize: '16px' }}>cancel_icon</Icon>
                      ) : null}
                      <Typography variant="subtitle1" color="alternate.main">
                        Job Details
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        borderLeft: '5px solid',
                        height: '40px',
                        gap: 1,
                        borderLeftColor:
                          values.city !== null &&
                          values.city !== '' &&
                          errors.city === undefined &&
                          values.work_location !== null &&
                          values.work_location !== '' &&
                          errors.work_location === undefined &&
                          values.state !== null &&
                          values.state !== '' &&
                          errors.state === undefined
                            ? 'alternate.main'
                            : 'gray-line.main',
                        transition: 'border-left-color 0.3s linear',
                      }}
                    >
                      {values.city !== null &&
                      values.city !== '' &&
                      errors.city === undefined &&
                      values.work_location !== null &&
                      values.work_location !== '' &&
                      errors.work_location === undefined &&
                      values.state !== null &&
                      values.state !== '' &&
                      errors.state === undefined ? (
                        <Icon sx={{ color: 'positive.main', fontSize: '16px' }}>
                          check_circle_icon
                        </Icon>
                      ) : null}
                      {errors.city !== undefined ||
                      errors.work_location !== undefined ||
                      errors.state !== undefined ? (
                        <Icon sx={{ color: 'error.main', fontSize: '16px' }}>cancel_icon</Icon>
                      ) : null}
                      <Typography variant="subtitle1" color="alternate.main">
                        Work Location
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        borderLeft: '5px solid',
                        height: '40px',
                        gap: 1,
                        borderLeftColor:
                          values.job_description !== null &&
                          values.job_description !== '' &&
                          errors.job_description === undefined
                            ? 'alternate.main'
                            : 'gray-line.main',
                        transition: 'border-left-color 0.3s linear',
                      }}
                    >
                      {values.job_description !== null &&
                      values.job_description !== '' &&
                      errors.job_description === undefined ? (
                        <Icon sx={{ color: 'positive.main', fontSize: '16px' }}>
                          check_circle_icon
                        </Icon>
                      ) : null}
                      {errors.job_description !== undefined ? (
                        <Icon sx={{ color: 'error.main', fontSize: '16px' }}>cancel_icon</Icon>
                      ) : null}
                      <Typography variant="subtitle1" color="alternate.main">
                        Job Description
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        borderLeft: '5px solid',
                        height: '40px',
                        gap: 1,
                        borderLeftColor:
                          values.qualification_id !== null &&
                          values.qualification_id !== '' &&
                          errors.qualification_id === undefined &&
                          values.year_of_experience !== null &&
                          values.year_of_experience !== '' &&
                          errors.year_of_experience === undefined &&
                          values.fresh_grade_placement !== null &&
                          values.fresh_grade_placement !== '' &&
                          errors.fresh_grade_placement === undefined
                            ? 'alternate.main'
                            : 'gray-line.main',
                        transition: 'border-left-color 0.3s linear',
                      }}
                    >
                      {values.qualification_id !== null &&
                      values.qualification_id !== '' &&
                      errors.qualification_id === undefined &&
                      values.year_of_experience !== null &&
                      values.year_of_experience !== '' &&
                      errors.year_of_experience === undefined &&
                      values.fresh_grade_placement !== null &&
                      values.fresh_grade_placement !== '' &&
                      errors.fresh_grade_placement === undefined ? (
                        <Icon sx={{ color: 'positive.main', fontSize: '16px' }}>
                          check_circle_icon
                        </Icon>
                      ) : null}
                      {errors.qualification_id !== undefined ||
                      errors.year_of_experience !== undefined ||
                      errors.fresh_grade_placement !== undefined ? (
                        <Icon sx={{ color: 'error.main', fontSize: '16px' }}>cancel_icon</Icon>
                      ) : null}
                      <Typography variant="subtitle1" color="alternate.main">
                        Job Requirements
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        borderLeft: '5px solid',
                        height: '40px',
                        gap: 1,
                        borderLeftColor:
                          values.job_advertisement_duration !== null &&
                          values.job_advertisement_duration !== '' &&
                          errors.job_advertisement_duration === undefined
                            ? 'alternate.main'
                            : 'gray-line.main',
                        transition: 'border-left-color 0.3s linear',
                      }}
                    >
                      {values.job_advertisement_duration !== null &&
                      values.job_advertisement_duration !== '' &&
                      errors.job_advertisement_duration === undefined ? (
                        <Icon sx={{ color: 'positive.main', fontSize: '16px' }}>
                          check_circle_icon
                        </Icon>
                      ) : null}
                      {errors.job_advertisement_duration !== undefined ? (
                        <Icon sx={{ color: 'error.main', fontSize: '16px' }}>cancel_icon</Icon>
                      ) : null}
                      <Typography variant="subtitle1" color="alternate.main">
                        Job Duration
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </Form>
          )
        }
      }}
    </Formik>
  )
}
export default JobAdForm
